import { useState, useEffect } from 'react';
import { useAuth } from '../stores/auth-context';
import { useMode } from '../stores/mode-context';
import { api } from '../lib/api';
import { useConfigQuery, useUpdateConfig } from '../hooks/useConfig';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';

export function SettingsPage() {
  const { isAdmin, isOperator, isAccountant } = useAuth();
  const { registrationEnabled, toggleRegistration: setRegistrationMode } = useMode();
  const { data: config, isLoading } = useConfigQuery();
  const updateConfig = useUpdateConfig();

  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [showQrConfirm, setShowQrConfirm] = useState(false);
  const [qrResult, setQrResult] = useState<string | null>(null);

  const canManage = isAdmin || isOperator || isAccountant;

  useEffect(() => {
    if (config) {
      setEmailSubject(config.email_subject);
      setEmailBody(config.email_body);
    }
  }, [config]);

  async function toggleRegistration() {
    if (!canManage) return;
    try {
      await setRegistrationMode(!registrationEnabled);
    } catch {
      // handled by api interceptor
    }
  }

  async function saveEmailSettings() {
    if (!isAdmin) return;
    updateConfig.mutate({
      email_subject: emailSubject,
      email_body: emailBody,
    });
  }

  const inputStyle = 'w-full rounded-lg border border-ct-border bg-ct-surface px-4 py-2.5 text-sm text-ct-text placeholder:text-ct-text-muted/50 focus:ring-2 focus:ring-ct-primary focus:ring-offset-1 focus:border-ct-primary focus:outline-none transition-all duration-200';

  if (isLoading) {
    return (
      <div>
        <div className="mb-4 inline-flex items-center gap-3 rounded-full bg-indigo-50 border border-indigo-100 px-5 py-2">
          <span className="h-2 w-2 rounded-full bg-ct-primary" />
          <span className="text-xs font-bold uppercase tracking-wider text-ct-primary">Конфигурация</span>
        </div>
        <h1 className="mb-6 text-3xl font-extrabold tracking-tight text-ct-text">Настройки</h1>
        <p className="text-ct-text-muted">Загрузка...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 inline-flex items-center gap-3 rounded-full bg-indigo-50 border border-indigo-100 px-5 py-2">
        <span className="h-2 w-2 rounded-full bg-ct-primary" />
        <span className="text-xs font-bold uppercase tracking-wider text-ct-primary">Конфигурация</span>
      </div>
      <h1 className="mb-6 text-3xl font-extrabold tracking-tight text-ct-text">Настройки</h1>

      <div className="max-w-2xl space-y-6">
        <div className="rounded-xl bg-ct-surface border border-ct-border shadow-ct-sm p-6">
          <h2 className="mb-3 text-lg font-bold text-ct-text">Включение режима регистрации на Конференцию</h2>

          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-ct-text">
                {registrationEnabled ? 'Включено' : 'Выключено'}
              </div>
              <div className="text-xs text-ct-text-muted mt-1">
                {registrationEnabled
                  ? 'При включении операторы смогут регистрировать участников в день проведения Конференции'
                  : 'Регистрация через QR заблокирована'}
              </div>
            </div>

            {canManage && (
              <button
                onClick={toggleRegistration}
                className={`rounded-full px-6 py-2 text-sm font-bold text-ct-on-primary shadow-ct-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md active:scale-[0.98] ${
                  registrationEnabled
                    ? 'bg-gradient-to-r from-red-500 to-red-600'
                    : 'bg-gradient-to-r from-ct-primary to-ct-secondary'
                }`}
              >
                {registrationEnabled ? 'Выключить' : 'Включить'}
              </button>
            )}
          </div>
        </div>

        {isAdmin && (
          <div className="rounded-xl bg-ct-surface border border-ct-border shadow-ct-sm p-6">
            <h2 className="mb-3 text-lg font-bold text-ct-text">Настройки письма с QR-кодом</h2>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-semibold text-ct-text">Тема письма</label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className={inputStyle}
                  placeholder="Тема письма"
                />
                <p className="mt-0.5 text-xs text-ct-text-muted">
                  Используйте {'{l_name}'} и {'{f_name}'} как плейсхолдеры
                </p>
              </div>

              <div>
                <label className="mb-1 block text-sm font-semibold text-ct-text">Текст письма (HTML)</label>
                <textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  rows={10}
                  className={`${inputStyle} font-mono resize-y`}
                  placeholder="HTML код письма"
                />
                <p className="mt-0.5 text-xs text-ct-text-muted">
                  Используйте {'{l_name}'} и {'{f_name}'} как плейсхолдеры
                </p>
              </div>

              <button
                onClick={saveEmailSettings}
                disabled={updateConfig.isPending}
                className="rounded-full bg-gradient-to-r from-ct-primary to-ct-secondary px-6 py-2.5 text-sm font-bold text-ct-on-primary shadow-ct-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md active:scale-[0.98] disabled:opacity-50"
              >
                {updateConfig.isPending ? 'Сохранение...' : 'Сохранить настройки письма'}
              </button>

              {updateConfig.isSuccess && (
                <p className="text-sm font-semibold text-emerald-600">Настройки сохранены</p>
              )}
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="rounded-xl bg-ct-surface border border-ct-border shadow-ct-sm p-6">
            <h2 className="mb-3 text-lg font-bold text-ct-text">Очистка QR-кодов</h2>
            <p className="mb-3 text-sm text-ct-text-muted">
              Удалить все сгенерированные файлы QR-кодов с диска. После отправки письма
              QR-коды больше не нужны.
            </p>
            <button
              onClick={() => setShowQrConfirm(true)}
              className="rounded-full bg-gradient-to-r from-red-500 to-red-600 px-6 py-2 text-sm font-bold text-white shadow-ct-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-ct-md active:scale-[0.98]"
            >
              Очистить QR коды
            </button>
            {qrResult && (
              <p className="mt-2 text-sm font-semibold text-emerald-600">{qrResult}</p>
            )}
          </div>
        )}

        <ConfirmDialog
          open={showQrConfirm}
          title="Очистить QR-коды?"
          message="Все файлы QR-кодов будут безвозвратно удалены с диска. Продолжить?"
          confirmLabel="Удалить"
          variant="danger"
          onConfirm={async () => {
            try {
              const res = await api.config.cleanQr();
              setQrResult(`Удалено файлов: ${res.deleted}`);
            } catch {
              setQrResult('Ошибка при удалении');
            }
            setShowQrConfirm(false);
          }}
          onCancel={() => setShowQrConfirm(false)}
        />
      </div>
    </div>
  );
}
